import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { notifications } from '@mantine/notifications';
import { ListSystemsQuery, RequestSystemLogsRequest, RequestSystemLogsResponse, UploadSystemArtifactsRequest, UploadSystemArtifactsResponse, } from '@features/systems/domain/systems.types';
import { systemService } from './systemService';
import { useSignalR } from '@hooks/useSignalR';

export const systemKeys = {
  // Base key for all system queries. Invalidating this refetches EVERYTHING system-related
  all: ['systems'] as const,
  // Parent key for all list queries. Invalidating refetches all system lists regardless of filters
  lists: () => [...systemKeys.all, 'list'] as const,
  // Filtered system list. Invalidating refetches only lists with matching filters
  list: (query: ListSystemsQuery) => [...systemKeys.lists(), query] as const,
  // Parent key for all detail queries. Invalidating refetches all individual system details
  details: () => [...systemKeys.all, 'detail'] as const,
  // Individual system data. Invalidating refetches this system's details and all its subqueries
  detail: (id: string) => [...systemKeys.details(), id] as const,
  detailSubQueries: {
    // Cadwell app history for a system. Invalidated when parent detail(id) is invalidated
    cadwellApps: (id: string) => [...systemKeys.detail(id), 'cadwell-apps'] as const,
    // System artifacts list for a system. Invalidated when parent detail(id) is invalidated
    systemArtifacts: (id: string) => [...systemKeys.detail(id), 'system-artifacts'] as const,
    // CadLink topology for a system. Invalidated when parent detail(id) is invalidated
    cadlinkTopology: (id: string) => [...systemKeys.detail(id), 'cadlink-topology'] as const,
    // Clients of a specific server in the CadLink topology. Nested under cadlinkTopology so
    // invalidating the topology also invalidates all cached server-client expansions
    serverClients: (id: string, serverCadLinkId: string) => [...systemKeys.detailSubQueries.cadlinkTopology(id), 'server-clients', serverCadLinkId] as const,
  },
  // Download URI for specific artifact. Invalidating refetches only searches with matching query
  artifactDownloadUri: (artifactId: string) => [...systemKeys.all, 'artifact-download-uri', artifactId] as const,
  // Spotlight search results. Invalidating refetches only searches with matching query
  spotlightSearch: (query: ListSystemsQuery) => [...systemKeys.all, 'spotlight-search', query] as const,
};

export const useRequestSystemLogs = () => {
  const queryClient = useQueryClient();

  return useMutation<RequestSystemLogsResponse, AxiosError, RequestSystemLogsRequest>({
    mutationFn: (request) => systemService.requestSystemLogs(request),
    onSuccess: (data, variables) => {
      // Invalidate system artifacts queries to show the new requested artifact
      queryClient.invalidateQueries({
        queryKey: [...systemKeys.detail(variables.systemId), 'system-artifacts'],
        exact: false
      });

      // Create initial notification with artifact ID - SignalR will update this as status changes
      const notificationId = `artifact-${data.artifactId}`;
      notifications.show({
        id: notificationId,
        title: 'Log Retrieval Started',
        message: data.message,
        color: 'blue',
      });
    },
  });
};

export const useRefreshSystemData = () => {
  const {registerActiveRefresh, unregisterActiveRefresh} = useSignalR();

  return useMutation<void, AxiosError, string>({
    mutationFn: (systemId) => systemService.refreshSystemData(systemId),
    onMutate: (systemId) => {
      // Register active refresh to prevent SignalR disconnection during saga
      registerActiveRefresh(systemId);
    },
    onSettled: (_data, error, systemId) => {
      // Unregister active refresh on completion (success or failure) so the loading state clears
      if (error) {
        unregisterActiveRefresh(systemId);
      }
    },
  });
};

export const useUploadSystemArtifacts = () => {
  const queryClient = useQueryClient();

  return useMutation<UploadSystemArtifactsResponse, AxiosError, UploadSystemArtifactsRequest>({
    mutationFn: (request) => systemService.uploadSystemArtifacts(request),
    onSuccess: (data, variables) => {
      // Invalidate all system artifacts queries for this system (regardless of pagination)
      queryClient.invalidateQueries({
        queryKey: [...systemKeys.detail(variables.systemId), 'system-artifacts'],
        exact: false
      });
      notifications.show({
        title: 'Upload Successful',
        message: data.message,
        color: 'green',
        autoClose: 5000,
      });
    },
  });
};

export const useDeleteSystemArtifact = (systemId: string) => {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, { artifactId: string; artifactName: string }>({
    mutationFn: ({artifactId}) => systemService.deleteSystemArtifact(artifactId),
    onSuccess: (_, variables) => {
      // Invalidate artifacts query to refresh the table
      queryClient.invalidateQueries({
        queryKey: systemKeys.detailSubQueries.systemArtifacts(systemId),
      });

      notifications.show({
        title: 'File Deleted',
        message: `Successfully deleted ${variables.artifactName}`,
        color: 'green',
        autoClose: 5000,
      });
    },
    onError: (error) => {
      let message = 'Failed to delete the file. Please try again.';
      if (error.response?.status === 403) {
        message = 'You do not have permission to delete this file.';
      } else if (error.response?.status === 404) {
        message = 'The file was not found. It may have already been deleted.';
      }

      notifications.show({
        title: 'Delete Failed',
        message,
        color: 'red',
        autoClose: 10000,
      });
    },
  });
};

export const systemListQueryOptions = (query: ListSystemsQuery) =>
  queryOptions({
    queryKey: systemKeys.list(query),
    queryFn: () => systemService.getSystemList(query),
    placeholderData: keepPreviousData,
  });

export const systemQueryOptions = (systemId: string) =>
  queryOptions({
    queryKey: systemKeys.detail(systemId),
    queryFn: () => systemService.getSystem(systemId),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

export const cadwellApplicationHistoryQueryOptions = (systemId: string) =>
  queryOptions({
    queryKey: systemKeys.detailSubQueries.cadwellApps(systemId),
    queryFn: () => systemService.getCadwellApplicationHistory(systemId),
  });

export const systemSpotlightSearchQueryOptions = (query: ListSystemsQuery, isEnabled: boolean) =>
  queryOptions({
    queryKey: systemKeys.spotlightSearch(query),
    queryFn: () => systemService.getSystemList(query),
    placeholderData: keepPreviousData,
    staleTime: 10000,
    enabled: isEnabled,
  });

export const systemArtifactsQueryOptions = (systemId: string) =>
  queryOptions({
    queryKey: systemKeys.detailSubQueries.systemArtifacts(systemId),
    queryFn: () => systemService.getSystemArtifacts({systemId}),
    refetchInterval: 60 * 1000,
    placeholderData: keepPreviousData,
  });

export const topologyQueryOptions = (systemId: string) => 
  queryOptions({
    queryKey: systemKeys.detailSubQueries.cadlinkTopology(systemId),
    queryFn: () => systemService.getCadLinkTopology(systemId),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

export const serverClientsQueryOptions = (systemId: string, serverCadLinkId: string) => queryOptions({
  queryKey: systemKeys.detailSubQueries.serverClients(systemId, serverCadLinkId),
  queryFn: () => systemService.getServerClients(systemId, serverCadLinkId),
  staleTime: 5 * 60 * 1000, // 5 minutes
});